import React, { useEffect } from 'react'
import { Box, Typography, Container, Stack, Paper } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useRouteError } from 'react-router-dom'

import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
  BUTTON_SIZE,
} from '../../../components/shared/button/button.constants'

import { styles } from './error-styles'

function ErrorBoundaryFallback({ error: propsError, isChunkLoadError: propsIsChunkLoadError }) {
  const { t } = useTranslation('common')
  const routeError = useRouteError()

  const error = propsError || routeError

  const isChunkLoadError =
    propsIsChunkLoadError ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('ChunkLoadError') ||
    error?.name === 'ChunkLoadError'

  useEffect(() => {
    if (isChunkLoadError) {
      console.warn(
        'Chunk loading error detected - likely a new deployment. Auto-reloading in 3 seconds...'
      )
      setTimeout(() => {
        window.location.reload()
      }, 3000)
    }
  }, [isChunkLoadError])

  const handleReload = () => {
    window.location.reload()
  }

  return (
    <Container maxWidth="md" sx={styles.container}>
      <Paper elevation={3} sx={styles.paper}>
        <Box sx={styles.colorBand} />
        <Box sx={styles.contentContainer}>
          <Box sx={styles.iconContainer}>
            <Box sx={styles.iconCircle}>
              <Box component="span" sx={styles.iconText}>
                !
              </Box>
            </Box>
          </Box>
          <Typography variant="h4" component="h1" gutterBottom sx={styles.title}>
            {isChunkLoadError
              ? t('common:errorBoundary.chunkLoadingTitle')
              : t('common:errorBoundary.title')}
          </Typography>
          <Typography variant="body1" sx={styles.description}>
            {isChunkLoadError
              ? t('common:errorBoundary.chunkLoadingDescription')
              : t('common:errorBoundary.description')}
          </Typography>

          {process.env.NODE_ENV === 'development' && error && (
            <Box
              sx={{
                textAlign: 'left',
                backgroundColor: 'grey.100',
                padding: 2,
                borderRadius: 1,
                marginBottom: 3,
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '11px',
                }}
              >
                {error.message || JSON.stringify(error, null, 2)}
              </Typography>
            </Box>
          )}

          <Stack sx={styles.buttonContainer}>
            <Button
              onClick={handleReload}
              variant={BUTTON_VARIANTS.CONTAINED}
              theme={BUTTON_THEME.PRIMARY}
              size={BUTTON_SIZE.MEDIUM}
              label={
                isChunkLoadError
                  ? t('common:errorBoundary.reloadNow')
                  : t('common:errorBoundary.tryAgain')
              }
            />
          </Stack>
          <Box sx={styles.supportSection}>
            <Typography variant="body2" sx={styles.supportText}>
              {t('common:errorBoundary.support')}
            </Typography>
            <Typography component="a" variant="body2" sx={styles.supportLink}>
              support@hapana.com
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  )
}

export default ErrorBoundaryFallback
